import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth, API } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { 
    Users, DollarSign, TrendingUp, TrendingDown, UserCheck, MessageSquare, 
    CalendarDays, ArrowRight, CheckCircle2, Clock, FileSpreadsheet, Printer, 
    Download, X, FileText, ChevronRight
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { printFinancialReport } from '@/utils/pdfExport';

const MONTH_NAMES = ['','Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];

const StatCard = ({ icon: Icon, label, value, sub, color, delay }) => (
    <div className={`stat-card animate-fade-in-up ${delay}`}>
        <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
                <p className="label-overline mb-1">{label}</p>
                <p className="text-2xl font-extrabold text-slate-900 truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{value}</p>
                {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
            </div>
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ml-3 ${color}`}>
                <Icon className="w-5 h-5" />
            </div>
        </div>
    </div>
);

export default function DashboardPage() {
    const { user } = useAuth();
    const { t } = useLang();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [regional, setRegional] = useState(null);
    const [loading, setLoading] = useState(true);

    // Export & Print modal state
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportYear, setExportYear] = useState(2026);
    const [exportMonth, setExportMonth] = useState('all');
    const [downloading, setDownloading] = useState(false);
    const [printing, setPrinting] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const [s, r] = await Promise.all([
                    axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
                    axios.get(`${API}/settings/regional`, { withCredentials: true })
                ]);
                setStats(s.data);
                setRegional(r.data);
            } catch {}
            setLoading(false);
        };
        load();
    }, []);

    const fmt = (n) => `Rp ${Number(n || 0).toLocaleString('id-ID')}`;

    // Handle Excel Download
    const handleDownloadExcel = async () => {
        setDownloading(true);
        try {
            const url = `${API}/finance/export-excel?year=${exportYear}${exportMonth !== 'all' ? `&month=${exportMonth}` : ''}`;
            const response = await axios.get(url, {
                responseType: 'blob',
                withCredentials: true
            });
            
            const blob = new Blob([response.data], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            const filename = `Laporan_Kas_RT06_Menanggal_${exportYear}${exportMonth !== 'all' ? `_Bulan_${exportMonth}` : ''}.xlsx`;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error('Gagal mengunduh Excel:', err);
            alert('Gagal mengunduh file Excel. Pastikan Anda memiliki hak akses pengurus RT.');
        } finally {
            setDownloading(false);
        }
    };

    // Handle Print Out / PDF preview
    const handlePrintOut = async () => {
        setPrinting(true);
        try {
            const mParam = exportMonth !== 'all' ? exportMonth : null;
            const [expRes, iuranRes] = await Promise.all([
                axios.get(`${API}/finance/expenses?year=${exportYear}${mParam ? `&month=${mParam}` : ''}`, { withCredentials: true }),
                axios.get(`${API}/finance/iuran?year=${exportYear}${mParam ? `&month=${mParam}` : ''}`, { withCredentials: true })
            ]);

            const expensesList = expRes.data || [];
            const iuranList = iuranRes.data || [];
            
            const totalIncome = iuranList.reduce((acc, cur) => acc + (cur.amount || 0), 0);
            const totalExpense = expensesList.reduce((acc, cur) => acc + (cur.amount || 0), 0);

            printFinancialReport(
                iuranList,
                expensesList,
                { total_income: totalIncome, total_expense: totalExpense },
                regional,
                exportYear,
                mParam ? Number(mParam) : null
            );
        } catch (err) {
            console.error('Gagal mencetak laporan:', err);
            alert('Gagal menyiapkan print out laporan kas.');
        } finally {
            setPrinting(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-20 w-full rounded-xl" />
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-lg" />)}
                </div>
            </div>
        );
    }

    const rtLabel = regional?.rt_number ? `RT ${regional.rt_number}/RW ${regional.rw_number}` : '';
    const locationLabel = [regional?.kelurahan, regional?.kecamatan, regional?.kota].filter(Boolean).join(', ');

    return (
        <div className="space-y-6" data-testid="dashboard-page">
            {/* Welcome Header */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 animate-fade-in-up">
                <div className="flex items-center gap-4">
                    {regional?.logo_url ? (
                        <img src={regional.logo_url} alt="Logo RT" className="w-14 h-14 object-contain rounded-lg border border-slate-100" />
                    ) : (
                        <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Users className="w-7 h-7 text-primary" />
                        </div>
                    )}
                    <div>
                        <p className="label-overline">{rtLabel || 'Sistem Informasi RT'} {locationLabel && `— ${locationLabel}`}</p>
                        <h1 className="text-xl font-extrabold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                            {t.welcome}, {user?.name}
                        </h1>
                        <p className="text-slate-500 text-sm mt-0.5">
                            {MONTH_NAMES[stats?.current_month]} {stats?.current_year} &bull; 72 Kepala Keluarga (KK)
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    {/* EXPORT TO EXCEL & PRINT OUT BUTTON */}
                    <button 
                        onClick={() => setShowExportModal(true)} 
                        data-testid="quick-export-excel"
                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-emerald-300 bg-emerald-50 text-[#065f46] text-sm font-semibold hover:bg-emerald-100 transition-colors shadow-xs"
                    >
                        <FileSpreadsheet className="w-4 h-4 text-emerald-700" /> 
                        Export Excel & Cetak
                    </button>
                    <button onClick={() => navigate('/finance')} data-testid="quick-record-payment"
                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors">
                        <DollarSign className="w-4 h-4" /> {t.recordPayment}
                    </button>
                    <button onClick={() => navigate('/citizens')} data-testid="quick-add-citizen"
                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">
                        <Users className="w-4 h-4" /> {t.addCitizen}
                    </button>
                    <button onClick={() => navigate('/meetings')} data-testid="quick-create-meeting"
                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">
                        <CalendarDays className="w-4 h-4" /> {t.createMeeting}
                    </button>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={Users} label={t.totalCitizens} value={stats?.total_citizens || 0}
                    sub="Total tepat 72 KK terdaftar"
                    color="bg-blue-50 text-blue-600" delay="animate-delay-100" />
                <StatCard icon={TrendingUp} label={t.iuranMonth} value={fmt(stats?.iuran_this_month)}
                    sub={`${stats?.iuran_paid_count || 0} KK bertransaksi`}
                    color="bg-green-50 text-green-600" delay="animate-delay-200" />
                <StatCard icon={TrendingDown} label={t.expenses} value={fmt(stats?.expense_this_month)}
                    sub={`${MONTH_NAMES[stats?.current_month]} ${stats?.current_year}`}
                    color="bg-orange-50 text-orange-600" delay="animate-delay-300" />
                <StatCard icon={DollarSign} label="Total Saldo Kas"
                    value={fmt(stats?.cumulative_balance)}
                    sub={stats?.cumulative_balance >= 0 ? 'Saldo positif (semua waktu)' : 'Saldo negatif'}
                    color={stats?.cumulative_balance >= 0 ? 'bg-teal-50 text-teal-600' : 'bg-red-50 text-red-600'}
                    delay="animate-delay-300" />
            </div>

            {/* Alert items */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                    { label: t.pendingGuests, value: stats?.pending_guests || 0, icon: UserCheck, path: '/guest-report', color: 'text-amber-600 bg-amber-50', testid: 'stat-pending-guests' },
                    { label: t.openComplaints, value: stats?.open_complaints || 0, icon: MessageSquare, path: '/complaints', color: 'text-red-600 bg-red-50', testid: 'stat-open-complaints' },
                    { label: t.upcomingMeetings, value: stats?.upcoming_meetings || 0, icon: CalendarDays, path: '/meetings', color: 'text-purple-600 bg-purple-50', testid: 'stat-upcoming-meetings' },
                ].map(item => (
                    <button key={item.label} onClick={() => navigate(item.path)} data-testid={item.testid}
                        className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-4 hover:shadow-md hover:-translate-y-0.5 transition-all text-left w-full">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${item.color}`}>
                            <item.icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                            <p className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{item.value}</p>
                            <p className="text-xs text-slate-500">{item.label}</p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-400" />
                    </button>
                ))}
            </div>

            {/* Recent Meetings */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden animate-fade-in-up">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                    <h2 className="font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                        {t.recentMeetings}
                    </h2>
                    <button onClick={() => navigate('/meetings')} data-testid="view-all-meetings"
                        className="text-primary text-sm font-medium flex items-center gap-1 hover:underline">
                        Lihat semua <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                </div>
                {!stats?.recent_meetings?.length ? (
                    <div className="px-6 py-10 text-center">
                        <CalendarDays className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                        <p className="text-slate-400 text-sm">{t.noMeetings}</p>
                    </div>
                ) : (
                    <ul className="divide-y divide-slate-100">
                        {stats.recent_meetings.map(m => (
                            <li key={m.id} className="px-6 py-4 flex items-start gap-4 hover:bg-slate-50 transition-colors">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                    m.status === 'upcoming' ? 'bg-purple-100' : 'bg-green-100'}`}>
                                    {m.status === 'upcoming' ? <Clock className="w-4 h-4 text-purple-600" /> : <CheckCircle2 className="w-4 h-4 text-green-600" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-slate-800 text-sm">{m.title}</p>
                                    <p className="text-xs text-slate-500 mt-0.5">{m.date} {m.time && `• ${m.time}`} {m.location && `• ${m.location}`}</p>
                                </div>
                                <span className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${
                                    m.status === 'upcoming' ? 'badge-upcoming' : m.status === 'completed' ? 'badge-completed' : 'badge-inactive'
                                }`}>{m.status === 'upcoming' ? t.upcoming : m.status === 'completed' ? t.completed : m.status}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* ─── MODAL EXPORT TO EXCEL & PRINT OUT ──────────────────────────────────── */}
            {showExportModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden flex flex-col">
                        
                        {/* Modal Header */}
                        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/80 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-emerald-100 text-[#065f46] flex items-center justify-center">
                                    <FileSpreadsheet className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                                        Export Laporan Kas & Cetak Print Out
                                    </h3>
                                    <p className="text-xs text-slate-500">
                                        Unduh format Excel (.xlsx) atau cetak laporan resmi RT 06
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowExportModal(false)}
                                className="w-8 h-8 rounded-full hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 space-y-5">
                            {/* Filter Tahun */}
                            <div>
                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                                    Pilih Tahun Anggaran
                                </label>
                                <div className="grid grid-cols-3 gap-2">
                                    {[2026, 2025, 2024].map(yr => (
                                        <button
                                            key={yr}
                                            onClick={() => setExportYear(yr)}
                                            className={`py-2 px-3 rounded-lg text-xs font-bold border transition-all ${
                                                exportYear === yr
                                                    ? 'bg-[#065f46] text-white border-[#065f46] shadow-xs'
                                                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                                            }`}
                                        >
                                            Tahun {yr}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Filter Bulan */}
                            <div>
                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                                    Pilih Cakupan Periode
                                </label>
                                <select
                                    value={exportMonth}
                                    onChange={(e) => setExportMonth(e.target.value)}
                                    className="w-full text-xs font-semibold px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-slate-800 focus:outline-none focus:border-[#065f46]"
                                >
                                    <option value="all">Seluruh Bulan (1 Tahun Penuh &mdash; Januari s/d Desember)</option>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                                        <option key={m} value={m}>
                                            Bulan {MONTH_NAMES[m]} {exportYear}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Info Format Dokumen */}
                            <div className="p-3.5 bg-emerald-50/60 rounded-xl border border-emerald-200 text-xs text-slate-600 space-y-1.5">
                                <p className="font-bold text-[#065f46] flex items-center gap-1.5">
                                    <FileText className="w-4 h-4" /> Kelengkapan Buku Kas (.xlsx):
                                </p>
                                <ul className="list-disc list-inside space-y-0.5 text-slate-600 pl-1">
                                    <li>Sheet 1: Rekapitulasi Kas & Saldo Kumulatif</li>
                                    <li>Sheet 2: Rincian Seluruh Transaksi Belanja & Nota</li>
                                    <li>Sheet 3: Penerimaan Iuran Warga (72 KK)</li>
                                    <li>Sheet 4: Register Data 72 Kepala Keluarga Menanggal</li>
                                </ul>
                            </div>
                        </div>

                        {/* Modal Footer Actions */}
                        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2.5">
                            <button
                                onClick={handlePrintOut}
                                disabled={printing || downloading}
                                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 transition-colors"
                            >
                                <Printer className="w-4 h-4 text-slate-600" />
                                {printing ? 'Menyiapkan Cetak...' : 'Print Out / Cetak Laporan'}
                            </button>
                            <button
                                onClick={handleDownloadExcel}
                                disabled={downloading || printing}
                                className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-xs font-bold bg-[#065f46] text-white hover:bg-[#064e3b] transition-colors shadow-sm"
                            >
                                <Download className="w-4 h-4" />
                                {downloading ? 'Mengunduh Excel...' : 'Unduh File Excel (.xlsx)'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
